import { Skeleton } from 'antd';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts/highstock';
import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { DefaultKeyEventsAndIntervalsEmptyChart } from 'components/organisms/template-insight/empty-state';
import { ProductiveHeading07 } from 'components/styles';
import {
  IntervalsAsAbsoluteValueTooltip,
  IntervalsAsDeviationTooltip,
  KeyEventsAsDeviationTooltip,
} from './chart-tooltip';

export enum ChartTypes {
  IntervalAsAbsoluteValue = 'intervalAsAbsoluteValue',
  IntervalAsDeviation = 'intervalAsDeviation',
  KeyEventAsDeviation = 'keyEventAsDeviation',
}
interface CustomPoint extends Highcharts.Point {
  point: any;
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  background-color: var(--white-100);
  box-shadow: var(--shadow-02);

  .highcharts-scrollbar {
    height: 0;
    opacity: 0;
    margin-top: var(--spacing-03);
    transition: opacity 0.5s;
  }

  &:hover .highcharts-scrollbar {
    opacity: 1;
  }
`;

const HeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: var(--spacing-05);
  align-items: center;
`;

const EmptyWrapper = styled.div`
  height: 280px;
  width: 100%;
  position: relative;
  div {
    position: absolute;
    bottom: 41px;
    left: 32px;
  }
`;

const Header = styled.h1`
  ${ProductiveHeading07};
  font-weight: var(--font-weight-bold) !important;
`;

const renderEmpty = (chartType) =>
  DefaultKeyEventsAndIntervalsEmptyChart({
    noDataDescriptionKey: `KeyEventsAndIntervals.${chartType}.empty_chart.description`,
  });

const isEmpty = (chartOptions) => {
  return (
    (chartOptions.series?.filter((dataItem) => dataItem.data.length > 0) ?? [])
      .length === 0
  );
};

export const KeyEventsAndIntervalsChart = ({
  chartOptions,
  chartType,
  isLoading,
}) => {
  const isChartEmpty = isEmpty(chartOptions);

  const finalChartOptions = {
    ...chartOptions,
    tooltip: {
      useHTML: true,
      formatter() {
        const chart = this as any as CustomPoint;
        const { y, category, options } = chart.point;

        return renderToStaticMarkup(
          chartType === ChartTypes.IntervalAsAbsoluteValue ? (
            <IntervalsAsAbsoluteValueTooltip
              intervalName={options.name}
              value={y}
              noOfProjects={options.noOfProjects}
              date={category}
            />
          ) : chartType === ChartTypes.IntervalAsDeviation ? (
            <IntervalsAsDeviationTooltip
              value={y}
              intervalName={options.name}
              noOfProjects={options.noOfProjects}
              date={category}
            />
          ) : ChartTypes.KeyEventAsDeviation ? (
            <KeyEventsAsDeviationTooltip
              value={y}
              keyEventName={options.name}
              noOfProjects={options.noOfProjects}
              date={category}
            />
          ) : (
            <></>
          ),
        );
      },
      borderWidth: 0,
      backgroundColor: 'transparent',
      shadow: false,
    },
  };

  if (isLoading) {
    return <Skeleton paragraph={{ rows: 6 }} active />;
  }

  return (
    <Wrapper>
      <HeaderWrapper>
        <Header>
          <FormattedMessage
            id={`KeyEventsAndIntervals.${chartType}.chart.title`}
          />
        </Header>
      </HeaderWrapper>
      {isChartEmpty ? (
        <EmptyWrapper>{renderEmpty(chartType)}</EmptyWrapper>
      ) : (
        <HighchartsReact highcharts={Highcharts} options={finalChartOptions} />
      )}
    </Wrapper>
  );
};
